// Most building models in the catalog stand on a thin square slab the size of their
// footprint: a tile of dirt under the machine-gun nest, a grey square under the missile
// defence, a bevelled plinth under the C-RAM. Set on the ground those read as boxes. This
// cuts the slab out of a model as it loads, sets what stood on it down onto the ground, and
// lays a ragged patch of worn, trampled earth under it instead, so the building sits in the
// land rather than on a tray.
import * as THREE from 'three';

/** Models whose pad is the point (a runway, a parade ground, a field, a landing pad), or units/mines/rubble: left alone. */
const KEEP_PAD = new Set([
  'airfield', 'staging_area', 'farm', 'fishery', 'helipad',
  'barrier', 'electric_fence', 'land_mine', 'sea_mine', 'fishing_boat',
  'rubble_small', 'rubble_medium', 'rubble_large',
  'soldier', 'conscript', 'rifleman', 'marksman', 'heavy_gunner', 'at_team',
  'tank', 'boat', 'destroyer', 'landing_craft', 'carrier', 'submarine',
  'attack_heli', 'jet', 'f16', 'scout_drone', 'fpv_drone', 'attack_drone', 'shahed',
  'cruise_missile', 'kalibr', 'emp_missile', 'ballistic', 'icbm', 'hgv', 'hgv_nuclear', 'nuke',
]);
/** Models built with no slab that still want the worn ground under them. */
const PATCH_ONLY = new Set(['missile_defence', 'air_defence', 'bunker', 'hq', 'c_ram', 'missile_battery', 'drone_interceptor', 'howitzer']);

/** A seeded random stream, so the patch is the same every load. */
function stream(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

let patchTex: THREE.CanvasTexture | null = null;

/** Worn earth and gravel, densest in the middle and breaking up into the grass at a ragged edge. */
function patchTexture(): THREE.CanvasTexture {
  if (patchTex) return patchTex;
  const n = 256, r = stream(271);
  const c = document.createElement('canvas');
  c.width = c.height = n;
  const g = c.getContext('2d')!;
  // blotches of earth, many and close in the middle, fewer and fainter toward the rim
  const earth = ['#4a3f2e', '#55482f', '#3e3526', '#5d5038', '#463c2c', '#6a5c42'];
  for (let i = 0; i < 1400; i++) {
    const a = r() * Math.PI * 2, d = Math.pow(r(), 0.7) * n * 0.48;
    const x = n / 2 + Math.cos(a) * d, y = n / 2 + Math.sin(a) * d;
    const fade = 1 - d / (n * 0.48);
    g.globalAlpha = Math.min(1, 0.25 + fade * 0.9) * (0.5 + r() * 0.5);
    g.fillStyle = earth[Math.floor(r() * earth.length)]!;
    const rad = 2 + r() * 9 * (0.4 + fade);
    g.beginPath();
    g.ellipse(x, y, rad, rad * (0.5 + r() * 0.5), r() * Math.PI, 0, Math.PI * 2);
    g.fill();
  }
  // gravel and stones
  for (let i = 0; i < 500; i++) {
    const a = r() * Math.PI * 2, d = Math.pow(r(), 0.9) * n * 0.42;
    const v = 90 + r() * 70;
    g.globalAlpha = 0.5 + r() * 0.5;
    g.fillStyle = `rgb(${v},${v * 0.95},${v * 0.86})`;
    g.fillRect(n / 2 + Math.cos(a) * d, n / 2 + Math.sin(a) * d, 1 + r() * 2.5, 1 + r() * 2);
  }
  // tyre and boot scuffs
  g.strokeStyle = '#2f281e';
  g.lineCap = 'round';
  for (let i = 0; i < 18; i++) {
    g.globalAlpha = 0.25 + r() * 0.25;
    g.lineWidth = 1.5 + r() * 2.5;
    const a = r() * Math.PI * 2, d = r() * n * 0.3;
    const x = n / 2 + Math.cos(a) * d, y = n / 2 + Math.sin(a) * d, b = r() * Math.PI;
    g.beginPath();
    g.moveTo(x, y);
    g.quadraticCurveTo(x + Math.cos(b) * 30, y + Math.sin(b) * 30, x + Math.cos(b + 0.4) * 60, y + Math.sin(b + 0.4) * 60);
    g.stroke();
  }
  g.globalAlpha = 1;
  patchTex = new THREE.CanvasTexture(c);
  patchTex.colorSpace = THREE.SRGBColorSpace;
  return patchTex;
}

/** Faces of the model, in the root's frame: three corners and the face's normal and area. */
interface Face { mesh: THREE.Mesh; tri: number; ys: [number, number, number]; xs: number[]; zs: number[]; ny: number; area: number }

function faces(root: THREE.Object3D): Face[] {
  root.updateMatrixWorld(true);
  const toRoot = root.matrixWorld.clone().invert();
  const out: Face[] = [];
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), n = new THREE.Vector3(), e = new THREE.Vector3();
  root.traverse(o => {
    if (!(o instanceof THREE.Mesh) || o instanceof THREE.SkinnedMesh) return;
    const m = toRoot.clone().multiply(o.matrixWorld);
    const pos = o.geometry.attributes.position!, idx = o.geometry.index;
    const count = idx ? idx.count / 3 : pos.count / 3;
    const at = (k: number) => (idx ? idx.getX(k) : k);
    for (let t = 0; t < count; t++) {
      a.fromBufferAttribute(pos, at(t * 3)).applyMatrix4(m);
      b.fromBufferAttribute(pos, at(t * 3 + 1)).applyMatrix4(m);
      c.fromBufferAttribute(pos, at(t * 3 + 2)).applyMatrix4(m);
      n.subVectors(b, a).cross(e.subVectors(c, a));
      const area = n.length() / 2;
      out.push({ mesh: o, tri: t, ys: [a.y, b.y, c.y], xs: [a.x, b.x, c.x], zs: [a.z, b.z, c.z], ny: area > 0 ? n.y / (2 * area) : 0, area });
    }
  });
  return out;
}

/**
 * Cuts a model's base slab out, if it has one, and returns how thick it was (0 if none).
 * A slab is a level of upward faces in the bottom fifth of the model that spans at least
 * three quarters of its footprint each way; everything at or below that level goes.
 */
export function stripSlab(name: string, root: THREE.Object3D): number {
  if (KEEP_PAD.has(name)) return 0;
  const fs = faces(root);
  if (!fs.length) return 0;
  let lo = Infinity, hi = -Infinity, x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
  for (const f of fs) {
    for (const y of f.ys) { lo = Math.min(lo, y); hi = Math.max(hi, y); }
    for (const x of f.xs) { x0 = Math.min(x0, x); x1 = Math.max(x1, x); }
    for (const z of f.zs) { z0 = Math.min(z0, z); z1 = Math.max(z1, z); }
  }
  const H = hi - lo;
  if (PATCH_ONLY.has(name)) {
    layPatch(name, root, x0, x1, z0, z1);
    return 0;
  }
  // the low level with the most upward-facing area is the slab's top
  const levels = new Map<number, number>();
  for (const f of fs) {
    const top = Math.max(...f.ys) - lo;
    if (f.ny > 0.9 && top < 0.2 * H) levels.set(Math.round(top * 200) / 200, (levels.get(Math.round(top * 200) / 200) ?? 0) + f.area);
  }
  let top = -1, best = 0;
  for (const [y, area] of levels) if (area > best) { best = area; top = y; }
  if (top < 0 || best < 0.5 * (x1 - x0) * (z1 - z0)) {
    if (PATCH_ONLY.has(name)) layPatch(name, root, x0, x1, z0, z1);
    return 0;
  }
  const onTop = fs.filter(f => f.ny > 0.9 && Math.abs(Math.max(...f.ys) - lo - top) < 0.006);
  const spanX = Math.max(...onTop.flatMap(f => f.xs)) - Math.min(...onTop.flatMap(f => f.xs));
  const spanZ = Math.max(...onTop.flatMap(f => f.zs)) - Math.min(...onTop.flatMap(f => f.zs));
  if (spanX < 0.75 * (x1 - x0) || spanZ < 0.75 * (z1 - z0)) return 0;
  // drop every face that lies wholly at or below the slab's top: its top, sides and underside
  const cut = lo + top + 0.006;
  const drop = new Map<THREE.Mesh, Set<number>>();
  for (const f of fs) {
    if (Math.max(...f.ys) <= cut) {
      let s = drop.get(f.mesh);
      if (!s) drop.set(f.mesh, s = new Set());
      s.add(f.tri);
    }
  }
  for (const [mesh, gone] of drop) {
    const geo = mesh.geometry;
    const idx = geo.index;
    const count = idx ? idx.count / 3 : geo.attributes.position!.count / 3;
    const keep: number[] = [];
    for (let t = 0; t < count; t++) {
      if (gone.has(t)) continue;
      for (let k = 0; k < 3; k++) keep.push(idx ? idx.getX(t * 3 + k) : t * 3 + k);
    }
    geo.setIndex(keep);
    geo.computeBoundingBox();
    geo.computeBoundingSphere();
  }
  // what stood on the slab now stands on the ground
  for (const c of root.children) c.position.y -= top;
  layPatch(name, root, x0, x1, z0, z1);
  return top;
}

/** Wears the ground round a model bare, in a ragged patch a little wider than its footprint. */
function layPatch(name: string, root: THREE.Object3D, x0: number, x1: number, z0: number, z1: number): void {
  const size = Math.max(x1 - x0, z1 - z0) * 1.15;
  const patch = new THREE.Mesh(
    new THREE.PlaneGeometry(size, size).rotateX(-Math.PI / 2),
    new THREE.MeshLambertMaterial({ map: patchTexture(), transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 }),
  );
  patch.position.set((x0 + x1) / 2, 0.004, (z0 + z1) / 2);
  patch.rotation.y = stream(name.length * 977 + name.charCodeAt(0))() * Math.PI * 2;
  patch.receiveShadow = true;
  patch.name = 'groundPatch';
  patch.userData.noRim = true;
  patch.geometry.userData.shared = true;
  (patch.material as THREE.Material).userData.shared = true;
  root.add(patch);
}
